import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, map, of, take } from 'rxjs';
import { OutletRightsDTO } from './model/outlet';

@Injectable({
  providedIn: 'root',
})
export class OutletCacheService {
  outletSelected$ = new Subject();
  private readonly currentOutletStorageKey = 'presto-outlet';
  private readonly currentOutletsStorageKey = 'presto-outlets-with-rights';
  private selectedOutlet$ = new BehaviorSubject<OutletRightsDTO>({
    outlet: {
      identity: {
        id: -1,
        name: '',
      },
      marketingChannel: {
        id: '',
        name: '',
      },
      country: {
        id: '',
        name: '',
      },
    },
    rights: [],
  });
  private outlets$ = new Observable<OutletRightsDTO[]>();

  constructor() {
    const outlet = JSON.parse(
      sessionStorage.getItem(this.currentOutletStorageKey) || 'null'
    ) as OutletRightsDTO;
    this.selectedOutlet$.next(outlet);

    const outlets = JSON.parse(
      sessionStorage.getItem(this.currentOutletsStorageKey) || 'null'
    ) as OutletRightsDTO[];
    this.outlets$ = of(outlets);
  }

  /**
   * Returns an observable that emits the current outlet selected
   * @returns Stream of current outlet info
   */
  getSelectedOutlet(): Observable<OutletRightsDTO> {
    return this.selectedOutlet$.asObservable();
  }

  /**
   * Saves the selected outlet in sessionStorage, and updates behaviorsubject
   * @param outlet Selected outlet
   */
  setSelectedOutlet(outlet: OutletRightsDTO): void {
    sessionStorage.setItem(
      this.currentOutletStorageKey,
      JSON.stringify(outlet)
    );
    this.selectedOutlet$.next(outlet);
  }

  /**
   * Returns an observable that emits all the outlets the current user has rights to
   * @returns Stream of current outlets info
   */
  getOutlets(): Observable<OutletRightsDTO[]> {
    return this.outlets$;
  }

  /**
   * Saves the outlets the user has access to in sessionStorage, and updates behaviorsubject
   * @param outlets Outlets the current user has rights to
   */
  setOutlets(outlets: OutletRightsDTO[]): void {
    sessionStorage.setItem(
      this.currentOutletsStorageKey,
      JSON.stringify(outlets)
    );
    this.outlets$ = of(outlets);
  }

  getOutlesForCountryAndRight(
    country: string,
    rightName: string
  ): Observable<OutletRightsDTO[]> {
    return this.getOutlets().pipe(
      map((outlets: OutletRightsDTO[]) => {
        return outlets.filter(
          (outlet: OutletRightsDTO) =>
            outlet.outlet.country.id === country &&
            outlet.rights.some((right) => right.name === rightName)
        );
      }),
      take(1)
    );
  }
}
